import {Box, Link, useResponsiveValue} from '@primer/react'
import {HeaderMenu, type HeaderMenuBaseProps} from './HeaderMenu'
import {LABELS, TEST_IDS} from '../../constants'
import {PageHeader} from '@primer/react/experimental'

import type {OptionConfig} from '../OptionConfig'
import {SafeHTMLBox, type SafeHTMLString} from '@github-ui/safe-html'
import {issueHovercardPath} from '@github-ui/paths'
import {graphql} from 'relay-runtime'
import type {HeaderViewer$key} from './__generated__/HeaderViewer.graphql'
import {useFragment} from 'react-relay'

export type HeaderViewerProps = {
  headerViewerKey: HeaderViewer$key
  optionConfig: OptionConfig
} & HeaderMenuBaseProps

export function HeaderViewer({headerViewerKey, optionConfig, ...rest}: HeaderViewerProps) {
  const data = useFragment(
    graphql`
      fragment HeaderViewer on Issue {
        titleHTML
        number
        url
        repository {
          name
          owner {
            login
          }
        }
        ...HeaderMenu
      }
    `,
    headerViewerKey,
  )
  const {titleHTML, number, url, repository} = data
  const {titleAs = 'h1'} = optionConfig

  const headerMenu = <HeaderMenu optionConfig={optionConfig} headerMenuData={data} {...rest} />

  const isNarrow = useResponsiveValue({narrow: true}, false)

  return (
    <Box aria-label="Header" role={'region'} data-testid={TEST_IDS.issueHeader}>
      <PageHeader
        sx={{
          gap: 2,
          px: 1,
        }}
      >
        {!!isNarrow && (
          <PageHeader.ContextArea>
            <PageHeader.ContextAreaActions sx={{flexGrow: 0, width: '100%', justifyContent: 'left'}}>
              {headerMenu}
            </PageHeader.ContextAreaActions>
          </PageHeader.ContextArea>
        )}

        <PageHeader.TitleArea sx={{pt: 2, display: 'flex', justifyContent: 'space-between'}}>
          <PageHeader.Title
            as={titleAs}
            sx={{display: 'inline', flexGrow: 1, lineHeight: '1.125', fontSize: '32px', fontWeight: 'normal'}}
          >
            <SafeHTMLBox
              as="bdi"
              sx={{display: 'inline', wordBreak: 'break-word', mr: 2}}
              className="markdown-title"
              html={titleHTML as SafeHTMLString}
              data-testid={TEST_IDS.issueTitle}
            />
            <Box sx={{display: 'inline-flex', whiteSpace: 'nowrap', alignItems: 'center', gap: 1}}>
              <Link
                href={url}
                target="_blank"
                data-hovercard-url={issueHovercardPath({
                  owner: repository.owner.login,
                  repo: repository.name,
                  issueNumber: number,
                })}
                sx={{color: 'fg.muted', fontWeight: 'light', display: 'inline'}}
              >
                {LABELS.issueNumber(number)}
              </Link>
            </Box>
          </PageHeader.Title>
          {!isNarrow && <PageHeader.Actions sx={{flex: '1 0 auto'}}>{headerMenu}</PageHeader.Actions>}
        </PageHeader.TitleArea>
      </PageHeader>
    </Box>
  )
}

try{ HeaderViewer.displayName ||= 'HeaderViewer' } catch {}